/**
 * ReplayTourLink Component
 *
 * A small link component that allows users to replay a dismissed tour.
 * Place this in page footers or headers where users might want to review
 * the tour content again.
 *
 * @example
 * // In a page component
 * import { ReplayTourLink } from '@/components/ReplayTourLink'
 *
 * function WorkflowPage() {
 *   const { TourComponent, replay } = useTour(workflowDataTour)
 *
 *   return (
 *     <>
 *       <PageContent />
 *       <TourComponent />
 *       <footer>
 *         <ReplayTourLink onReplay={replay} />
 *       </footer>
 *     </>
 *   )
 * }
 */

import { Typography } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'

const { Link } = Typography

export interface ReplayTourLinkProps {
  /** Callback to trigger tour replay */
  onReplay: () => void
  /** Optional custom link text (defaults to "Replay tour") */
  text?: string
  /** Optional className for styling */
  className?: string
  /** Show icon before text (defaults to true) */
  showIcon?: boolean
}

/**
 * ReplayTourLink
 *
 * A small, unobtrusive link for replaying dismissed tours.
 * Uses Ant Design Link component for consistent styling.
 */
export function ReplayTourLink({
  onReplay,
  text = 'Replay tour',
  className,
  showIcon = true,
}: ReplayTourLinkProps) {
  return (
    <Link
      onClick={onReplay}
      className={className}
      style={{ fontSize: '12px', color: '#8c8c8c' }}
    >
      {showIcon && <QuestionCircleOutlined style={{ marginRight: 4 }} />}
      {text}
    </Link>
  )
}

export default ReplayTourLink
